import MarginOutlinedIcon from "@mui/icons-material/MarginOutlined"
import PaddingOutlinedIcon from "@mui/icons-material/PaddingOutlined"
import Box from "@mui/material/Box"
import { ReactNode } from "react"
import type { AnySceneObjectT } from "tweenly"
import { getStyleFieldLabel } from "../../../../helpers/properties.helpers"
import { useLinkedStyles } from "../../../../hooks/useLinkedStyles"
import ExpandableSection from "../../../common/ExpandableSection"
import IndependentToggle from "../../../common/PropertyTools/IndependentToggle"
import PrefixedInput from "../../../common/PropertyTools/PrefixedInput"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"

interface Props {
    object: AnySceneObjectT
    handleSetValue: (property: any, value: any) => void
}

/** Top | bottom, left | right — the order the sides' own fields are laid out in. */
const SIDES = ["Top", "Bottom", "Left", "Right"] as const

interface GroupProps extends Props {
    group: "margin" | "padding"
    toggleTitle: string
    icon: ReactNode
}

/**
 * One of the two groups, laid out like the corner radius in Appearance: a shared field for
 * all four sides with its toggle at the end, and the four sides below while it is on.
 */
const SidesGroup = ({ object, handleSetValue, group, toggleTitle, icon }: GroupProps) => {
    const sides = useLinkedStyles(
        object,
        SIDES.map((side) => group + side),
        handleSetValue
    )
    if (sides.styles.length === 0) return null

    const name = group === "margin" ? "Margin" : "Padding"
    // a negative margin pulls neighbours in; a negative padding means nothing
    const min = group === "padding" ? 0 : undefined

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <PropertyRow
                gutter={
                    <IndependentToggle
                        title={toggleTitle}
                        icon={icon}
                        active={sides.independent}
                        onClick={sides.toggleIndependent}
                    />
                }
            >
                <Box display="flex" gap={1}>
                    <Box flex={1} minWidth={0}>
                        <PrefixedInput
                            name={group}
                            value={sides.sharedValue}
                            placeholder={sides.isMixed ? "Mixed" : undefined}
                            label={`${group} (${sides.unit})`}
                            tooltip={`${name} (${sides.unit})`}
                            handleOnChange={sides.handleAllChange}
                            min={min}
                        />
                    </Box>
                    <Box flex={1} />
                </Box>
            </PropertyRow>
            {sides.independent && (
                <PropertyRow>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                        {sides.styles.map(({ property, style }) => (
                            <Box key={property} minWidth={0}>
                                <PrefixedInput
                                    name={property}
                                    value={style.value}
                                    label={getStyleFieldLabel(style)}
                                    tooltip={`${name} ${property
                                        .slice(group.length)
                                        .toLowerCase()} (${style.unit ?? sides.unit})`}
                                    handleOnChange={sides.handleOneChange}
                                    min={min}
                                />
                            </Box>
                        ))}
                    </Box>
                </PropertyRow>
            )}
        </Box>
    )
}

/**
 * Margin and padding, one group under the other, each with its own shared field and its own
 * toggle — the same control as the corner radius.
 */
const MarginAndPadding = ({ object, handleSetValue }: Props) => {
    const hasSides = object.styles.some((style) =>
        /^(margin|padding)(Top|Bottom|Left|Right)$/.test(style.property)
    )
    if (!hasSides) return <></>

    return (
        <ExpandableSection
            expanded={false}
            title={"Margin & padding"}
            component={
                <Box display="flex" flexDirection="column" gap={1.5} width="100%" pb={0.5}>
                    <SidesGroup
                        object={object}
                        handleSetValue={handleSetValue}
                        group="margin"
                        toggleTitle="Independent margins"
                        icon={<MarginOutlinedIcon fontSize="small" />}
                    />
                    <SidesGroup
                        object={object}
                        handleSetValue={handleSetValue}
                        group="padding"
                        toggleTitle="Independent padding"
                        icon={<PaddingOutlinedIcon fontSize="small" />}
                    />
                </Box>
            }
        />
    )
}

export default MarginAndPadding
